import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Calendar from 'react-calendar';
import Api from '../Auth/Api';
import { decodeJWT } from '../Components/jwtUtils';

// Componentes
import ActionButtons from '../Components/Dashboard/ActionButtons';
import CourseSelector from '../Components/Dashboard/CourseSelector';
import AulasManager from '../Components/Dashboard/Aulas/AulasManager';
import UsuariosManager from '../Components/Dashboard/Usuarios/UsuariosManager';
import TopicosManager from '../Components/Dashboard/Topicos/TopicosManager';
import CursosManager from '../Components/Dashboard/Cursos/CursosManager';
import SistemaInfo from '../Components/Dashboard/SistemaInfo';

function Dashboard() {
    const token = JSON.parse(localStorage.getItem("user_token"))
    const nivel = decodeJWT(token).nivel;

    const [date, setDate] = useState(new Date());
    
    // Função para detectar se é desktop (1400px+)
    const isDesktop = () => {
        return window.innerWidth >= 1400;
    };
    // Estado para o curso selecionado
    const [idCursoInicio, setIdCursoInicio] = useState(null);
    const [dataModulosInicio, setDataModulosInicio] = useState([]);

    // Estado para armazenar as informações do sistema
    const [infoSistema, setInfoSistema] = useState({
        total_alunos: 0,
        total_cursos: 0,
        total_certificados: 0,
        total_aulas_concluidas: 0
    });

    // Inicialização dos gerenciadores
    const aulasManager = AulasManager({ token });
    const usuariosManager = UsuariosManager({ token, nivel });
    const topicosManager = TopicosManager({ token, nivel });
    const cursosManager = CursosManager({ token, nivel });

    // Efeito para buscar as informações do sistema
    useEffect(() => {
        async function fetchSistemaInfo() {
            if (nivel === 'Administrador' || nivel === 'Gestor' || nivel === 'Moderador') {
                try {
                    const response = await Api.get(`api/sistema/info`, {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    });
                    setInfoSistema(response.data.data);
                } catch (error) {
                    console.error("Erro ao buscar informações do sistema:", error);
                }
            }
        }
        fetchSistemaInfo();
    }, [token, nivel]);

    // Efeito para buscar módulos quando um curso é selecionado
    useEffect(() => {
        if (idCursoInicio) {
            async function fetchModulos() {
                try {
                    const response = await Api.get(`api/listar-modulos/${idCursoInicio}`, {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    });
                    setDataModulosInicio(response.data);
                } catch (error) {
                    console.error(error.response?.data?.message || error);
                    setDataModulosInicio([]);
                }
            }
            fetchModulos();
        } else {
            setDataModulosInicio([]);
        }
    }, [token, idCursoInicio]);

    const [dataAulasAdicionadas, setDataAulasAdicionadas] = useState([]);
    useEffect(() => {
        async function fetchData() {
            try {
                const response = await Api.get(`api/ultimas-aulas`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataAulasAdicionadas(response.data);
            } catch (error) {
                console.error(error);
            }
        }
        fetchData();
    }, [token]);

    // Handler para alterar curso selecionado
    const handleCursoChange = (selectedOption) => {
        if (selectedOption) {
            setIdCursoInicio(selectedOption.value);
        } else {
            setIdCursoInicio(null);
        }
    };

    // Componente do Calendário
    const CalendarComponent = ({ className }) => (
        <div className={className}>
            <div className="pb-3">
                <Calendar
                    formatShortWeekday={(locale, date) => date.toLocaleString(locale, { weekday: 'short' }).slice(0, 3)}
                    onChange={setDate}
                    value={date}
                />
                <div className="my-3">
                    <Link to="/calendario">
                        <button
                            type="button"
                            className="btn btn-outline-primary rounded py-2 w-100"
                            tabIndex="0"
                        >
                            Ver calendário completo
                        </button>
                    </Link>
                </div>
            </div>
        </div>
    );

    return (
        <div className="container-fluid home mt-3">
            <div className="row">
                {/* SistemaInfo - 100% em notebook, 8/12 em desktop */}
                <div className="col-12 col-xxl-8">
                    {(nivel === 'Administrador' || nivel === 'Gestor' || nivel === 'Moderador') && (
                        <SistemaInfo infoSistema={infoSistema} />
                    )}
                </div>

                {/* Calendário - Renderização condicional baseada no tamanho da tela */}
                {isDesktop() && (
                    // Desktop: Calendário ao lado do SistemaInfo
                    <CalendarComponent className="col-12 col-xxl-4" />
                )}

                {/* ActionButtons e CourseSelector - Logo após SistemaInfo em notebook */}
                <div className="col-12 col-xxl-8">
                    <ActionButtons
                        nivel={nivel}
                        aulasManager={aulasManager}
                        cursosManager={cursosManager}
                        usuariosManager={usuariosManager}
                    />

                    <CourseSelector
                        dataCursos={cursosManager.dataCursos}
                        dataModulosInicio={dataModulosInicio}
                        handleCursoChange={handleCursoChange}
                        customStyles={cursosManager.customStyles}
                    />
                </div>

                {/* Calendário - Notebook: ao lado do Novas Aulas */}
                {!isDesktop() && (
                    <CalendarComponent className="col-12 col-lg-6" />
                )}

                {/* Novas Aulas - Grid 2x2 em notebook, embaixo do Calendário em desktop */}
                {(nivel === 'Administrador' || nivel === 'Moderador') && (
                    <div className="col-12 col-lg-6 col-xxl-4">
                        <div className="classes rounded bg-primary flex-fill text-white p-4">
                            <span className="d-flex justify-content-center fs-5 fw-500 text-center">
                                Novas Aulas
                            </span>
                            <div className="container-fluid mt-2">
                                <div className="row">
                                    {dataAulasAdicionadas.map((value) => (
                                        <div
                                            key={value.id}
                                            className="d-flex flex-column rounded bg-white text-black text-dark p-3 my-2"
                                        >
                                            <span className="fw-normal fs-7 text-pretty">{value.nome_curso}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Renderiza o modal de curso */}
                {cursosManager.renderModal(topicosManager)}

                {/* Renderiza os modais de aula e módulo */}
                {aulasManager.renderModals(cursosManager.dataCursos)}

                {/* Renderiza o modal de usuário */}
                {usuariosManager.renderModal(cursosManager.dataCursos)}

                {/* Renderiza o modal de tópico (apenas para Administrador) */}
                {nivel === 'Administrador' && topicosManager.renderModal(cursosManager.setShowModalAddCurso)}
            </div>
        </div>
    );
}

export default Dashboard;